import { useCallback } from 'react';
import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useMutation, useApolloClient } from '@apollo/client/react';
import first from 'lodash/first';
import RefreshTokenService from 'services/RefreshTokenService';
import { formatGraphqlErrors } from 'utils/helpers';
import requireValue from 'utils/requireValue';
import {
  ProjectsDocument,
  MeDocument,
  type MeQuery,
  type CreateUserMutation,
  type LoginWithPasswordMutation,
} from 'graphql/generated/graphql';

/** The sign-up and login mutations; both answer with the refresh token and the user. */
type TAuthMutation = CreateUserMutation | LoginWithPasswordMutation;

export type TAuthResult = NonNullable<
  CreateUserMutation['createUser'] | LoginWithPasswordMutation['loginWithPassword']
>;

const authResultOf = (data: TAuthMutation) => first(Object.values(data));

const useAuth = <TData extends TAuthMutation, TVariables extends OperationVariables>(
  mutation: TypedDocumentNode<TData, TVariables>,
): [(variables: TVariables) => Promise<TAuthResult>, boolean] => {
  const [authMutation, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: ProjectsDocument }],
  });
  const client = useApolloClient();
  const auth = useCallback(
    async (variables: TVariables) => {
      try {
        const res = await authMutation({ variables });
        const resultData = requireValue(
          authResultOf(requireValue(res.data, 'data')),
          'refreshToken',
        );
        const { refreshToken, user } = resultData;

        if (refreshToken) {
          RefreshTokenService.setRefreshToken(refreshToken);
        }

        client.writeQuery<MeQuery>({ query: MeDocument, data: { me: user } });

        return resultData;
      } catch (error) {
        const e = formatGraphqlErrors(error);
        throw e;
      }
    },
    [authMutation, client],
  );

  return [auth, loading];
};

export default useAuth;
